import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';
import { FormikValues } from 'formik';

import api from '../../../utils/api';
import { GET_ADMIN_SCHOLARSHIP_CONFIG_QUERY_KEY } from './useGetAdminScholarshipConfigQuery';

export interface ConfigureScholarshipPayload {
  isEnabled: boolean;
  eligibilityTrigger: number;
  allowedFileExtensions: string;
  notificationRoles: string;
}

interface ConfigureScholarshipResponse {
  code: number;
  message: string;
  data?: unknown;
}

const parseBooleanValue = (value: unknown) => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }

  return Boolean(value);
};

const parseStringValue = (value: unknown) => {
  if (Array.isArray(value)) {
    return value.join(',');
  }

  return value === null || typeof value === 'undefined' ? '' : String(value);
};

export const useConfigureScholarshipMutation = (handleClose?: () => void) => {
  const queryClient = useQueryClient();

  const configureScholarship = async (
    payload: ConfigureScholarshipPayload,
  ): Promise<ConfigureScholarshipResponse> => {
    return (await api.post('/admin/scholarship/config', payload))?.data;
  };

  const mutation = useMutation({
    mutationKey: ['configure-scholarship'],
    mutationFn: configureScholarship,
  });

  const configureScholarshipHandler = async (values: FormikValues, resetForm?: () => void) => {
    const payload: ConfigureScholarshipPayload = {
      isEnabled: parseBooleanValue(values?.isEnabled),
      eligibilityTrigger: Number(values?.eligibilityTrigger ?? 0),
      allowedFileExtensions: parseStringValue(values?.allowedFileExtensions),
      notificationRoles: parseStringValue(values?.notificationRoles),
    };

    try {
      await mutation.mutateAsync(payload, {
        onSuccess: (data) => {
          notification.success({
            message: data?.message || 'Scholarship configuration saved successfully',
          });

          queryClient.refetchQueries({
            queryKey: GET_ADMIN_SCHOLARSHIP_CONFIG_QUERY_KEY,
          });

          resetForm?.();
          handleClose?.();
        },
      });
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description:
          error?.response?.data?.message ||
          error?.response?.data?.title ||
          'Failed to save scholarship configuration',
      });
    }
  };

  return {
    configureScholarshipHandler,
    isConfigureScholarshipLoading: mutation.isPending,
    isLoading: mutation.isPending,
  };
};
